'use client'

import { memo } from 'react'
import {
  Brain,
  CalendarClock,
  KeyRound,
  Keyboard,
  Mic,
  Plug,
  Search,
  Smartphone,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { NinxlStaggerReveal, NinxlStaggerItem } from './ninxl-scroll-reveal'
import {
  NinxlCapabilityAssetMock,
  type NinxlCapabilityAssetPlacement,
} from './ninxl-capability-asset-mock'
import { cn } from '@/lib/utils'

type FeatureHighlight = {
  text: string
  icon: LucideIcon
  category: string
  accent: string
  assetPlacement: NinxlCapabilityAssetPlacement
}

function contentPaddingForPlacement(placement: NinxlCapabilityAssetPlacement): string {
  if (placement === 'left-mid-ccw') {
    return 'pl-14 sm:pl-16 md:pl-[4.5rem] pr-5'
  }
  return 'pr-14 sm:pr-16 md:pr-[4.5rem]'
}

const FEATURE_HIGHLIGHTS: FeatureHighlight[] = [
  {
    text: 'On-device inference with warm models, instant responses, and cancel anytime',
    icon: Brain,
    category: 'Inference',
    accent: 'border-[#21728d]/25 bg-[#21728d]/5',
    assetPlacement: 'right-mid-cw',
  },
  {
    text: 'On-device voice. Talk to your agent locally with STT, chat, TTS, and barge-in',
    icon: Mic,
    category: 'Voice',
    accent: 'border-rose-500/20 bg-rose-500/5',
    assetPlacement: 'corner-top-ccw',
  },
  {
    text: 'InkFlow: dictate, instruct, or transform text in any app with global hotkeys and on-device Whisper',
    icon: Keyboard,
    category: 'InkFlow',
    accent: 'border-fuchsia-500/20 bg-fuchsia-500/5',
    assetPlacement: 'right-low-cw',
  },
  {
    text: 'Local web search & fetch that never leaves your device by default',
    icon: Search,
    category: 'Tools',
    accent: 'border-sky-500/20 bg-sky-500/5',
    assetPlacement: 'right-high-ccw',
  },
  {
    text: 'NinCrew, browser agents, and automations: each asks permission per domain or tool',
    icon: Users,
    category: 'Agents',
    accent: 'border-violet-500/20 bg-violet-500/5',
    assetPlacement: 'left-mid-ccw',
  },
  {
    text: 'Plug in your own keys or use hosted plugins only when you want',
    icon: KeyRound,
    category: 'Privacy',
    accent: 'border-amber-500/20 bg-amber-500/5',
    assetPlacement: 'right-deep-cw',
  },
  {
    text: 'Scheduled automations that run locally on your desktop',
    icon: CalendarClock,
    category: 'Automations',
    accent: 'border-orange-500/20 bg-orange-500/5',
    assetPlacement: 'corner-bottom-cw',
  },
  {
    text: 'MCP servers. Connect approved local tool servers from settings',
    icon: Plug,
    category: 'Integrations',
    accent: 'border-indigo-500/20 bg-indigo-500/5',
    assetPlacement: 'right-center-ccw',
  },
  {
    text: 'Background sessions + seamless handoff to phone or web',
    icon: Smartphone,
    category: 'Sync',
    accent: 'border-emerald-500/20 bg-emerald-500/5',
    assetPlacement: 'right-upper-cw',
  },
]

export const NinxlFeatureHighlights = memo(function NinxlFeatureHighlights() {
  return (
    <NinxlStaggerReveal
      className="ninxl-capabilities-grid mx-auto grid max-w-5xl grid-cols-1 gap-3 sm:grid-cols-2 min-[720px]:grid-cols-3"
      stagger={0.06}
    >
      {FEATURE_HIGHLIGHTS.map((item) => (
        <NinxlStaggerItem key={item.text} className="h-full">
          <div
            className={cn(
              'group ninxl-feature-tile ninxl-capability-tile relative flex h-full min-h-[108px] items-start gap-3.5 overflow-hidden rounded-2xl border px-5 py-4 text-sm text-slate-700 transition hover:border-[#21728d]/30',
              item.accent
            )}
          >
            <NinxlCapabilityAssetMock icon={item.icon} placement={item.assetPlacement} />
            <div className="absolute right-3 top-3 z-[1] text-[9px] font-semibold uppercase tracking-wider text-[#88a1ac]/80">
              {item.category}
            </div>
            <div
              className={cn(
                'relative z-[1] flex min-w-0 flex-1 items-start gap-3.5',
                contentPaddingForPlacement(item.assetPlacement),
              )}
            >
              <div className="ninxl-capability-icon mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center self-start rounded-xl border border-white/60 bg-white/80 text-[#21728d] shadow-sm transition duration-300 group-hover:scale-110">
                <item.icon className="h-4 w-4" />
              </div>
              <span className="relative min-w-0 flex-1 leading-snug pt-0.5">{item.text}</span>
            </div>
          </div>
        </NinxlStaggerItem>
      ))}
    </NinxlStaggerReveal>
  )
})